import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  DevicePort,
  PhysicalConnection,
  PhysicalConnectionInsert,
} from "@production-hub/shared";

/**
 * Fetch all available device ports from all connected devices.
 */
export const useBridgePorts = (bridgeUrl: string | undefined | null) => {
  return useQuery({
    queryKey: ["bridge-ports", bridgeUrl],
    queryFn: async (): Promise<DevicePort[]> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/physical-connections/ports`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch ports: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000,
  });
};

/**
 * Fetch all physical connections (wires) from a bridge.
 */
export const useBridgePhysicalConnections = (
  bridgeUrl: string | undefined | null,
) => {
  return useQuery({
    queryKey: ["bridge-physical-connections", bridgeUrl],
    queryFn: async (): Promise<PhysicalConnection[]> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/physical-connections`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch physical connections: ${response.status}`,
        );
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000,
  });
};

/**
 * Create a new physical connection (wire).
 */
export const useCreatePhysicalConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: PhysicalConnectionInsert,
    ): Promise<PhysicalConnection> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/physical-connections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error ||
            `Failed to create physical connection: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-physical-connections", bridgeUrl],
      });
      toast.success("Physical connection created");
    },
    onError: (error) => {
      toast.error("Failed to create physical connection: " + error.message);
    },
  });
};

/**
 * Delete a physical connection (wire).
 */
export const useDeletePhysicalConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/physical-connections/${id}`,
        { method: "DELETE" },
      );

      if (!response.ok && response.status !== 204) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error ||
            `Failed to delete physical connection: ${response.status}`,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-physical-connections", bridgeUrl],
      });
      toast.success("Physical connection deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete physical connection: " + error.message);
    },
  });
};
